import { useState, useMemo, useEffect } from 'react';
import { NAVY } from '../utils/constants';
import { Badge, Button } from './ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { STATUSES, typeLabel, toDisplayDate, toHHMM } from './Events';
import { fmt12 } from './TimeField';

// ─── Month grid (Stage 5) ────────────────────────────────────────────────────
// Built for a seasonal year: most months are empty and December is full.
// An empty month is a complete grid with a line saying so and a jump to the
// nearest month that has something; a heavy month caps each day at a few
// chips and shows a count for the rest. The status filter carries over from
// the list. The grid works from the rows the list already loaded, so opening
// it costs no query at all, and paging months is in-memory.
//
// Multi-day events sit on every day they cover. Only wide screens render
// this; the list stays the phone's view (see .ev-view-toggle in App.css).

const MAX_CHIPS = 3;             // chips per day before the count takes over
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const pad2 = (n) => String(n).padStart(2, '0');
const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return isoOf(d); };
const monthLabel = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
const shiftMonth = (ym, n) => { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`; };
const lastDayOf = (ev) => ev.end_date || ev.event_date;
const statusOf = (v) => STATUSES.find(s => s.value === v) || { label: v || '', tone: 'grey' };

// 42 cells, Monday first, covering the month plus the days that square it off.
const buildCells = (ym) => {
  const first = `${ym}-01`;
  const lead = (new Date(`${first}T00:00:00`).getDay() + 6) % 7; // Mon = 0
  const start = addDays(first, -lead);
  return Array.from({ length: 42 }, (_, i) => { const iso = addDays(start, i); return { iso, inMonth: iso.slice(0, 7) === ym }; });
};

export default function EventMonth({ rows, today, onOpen, filterLabel }) {
  const [ym, setYm] = useState(() => {
    try { const s = sessionStorage.getItem('ev-month'); if (s && /^\d{4}-\d{2}$/.test(s)) return s; } catch (e) { /* ignore */ }
    return today.slice(0, 7);
  });
  const [selected, setSelected] = useState(null);
  useEffect(() => { try { sessionStorage.setItem('ev-month', ym); } catch (e) { /* ignore */ } }, [ym]);

  const cells = useMemo(() => buildCells(ym), [ym]);

  // One pass over the rows: every event lands on each grid day it covers.
  const byDay = useMemo(() => {
    const map = new Map();
    const gridStart = cells[0].iso, gridEnd = cells[41].iso;
    const sorted = [...rows].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '') || a.title.localeCompare(b.title));
    sorted.forEach(ev => {
      const from = ev.event_date < gridStart ? gridStart : ev.event_date;
      const to = lastDayOf(ev) > gridEnd ? gridEnd : lastDayOf(ev);
      for (let d = from; d <= to; d = addDays(d, 1)) {
        if (!map.has(d)) map.set(d, []);
        map.get(d).push(ev);
      }
    });
    return map;
  }, [rows, cells]);

  const inMonth = useMemo(() => rows.filter(ev => ev.event_date.slice(0, 7) <= ym && lastDayOf(ev).slice(0, 7) >= ym), [rows, ym]);

  // For an empty month: the nearest months either side that have something.
  const nearest = useMemo(() => {
    if (inMonth.length) return null;
    const months = [...new Set(rows.map(ev => ev.event_date.slice(0, 7)))].sort();
    const next = months.find(m => m > ym);
    const prev = [...months].reverse().find(m => m < ym);
    const count = (m) => rows.filter(ev => ev.event_date.slice(0, 7) === m).length;
    return { next: next ? { ym: next, count: count(next) } : null, prev: prev ? { ym: prev, count: count(prev) } : null };
  }, [rows, ym, inMonth.length]);

  const go = (next) => { setYm(next); setSelected(null); };
  const dayEvents = selected ? (byDay.get(selected) || []) : [];
  const weekdayOf = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'long' });
  const timeText = (ev) => { const a = toHHMM(ev.start_time), b = toHHMM(ev.end_time); return a && b ? `${fmt12(a)} to ${fmt12(b)}` : a ? `from ${fmt12(a)}` : ''; };

  return (
    <div className="ev-month">
      <div className="ev-month-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Button variant="secondary" size="sm" aria-label="Previous month" onClick={() => go(shiftMonth(ym, -1))}><ChevronLeft size={16} strokeWidth={2} aria-hidden="true" /></Button>
          <Button variant="secondary" size="sm" aria-label="Next month" onClick={() => go(shiftMonth(ym, 1))}><ChevronRight size={16} strokeWidth={2} aria-hidden="true" /></Button>
          {ym !== today.slice(0, 7) && <Button variant="ghost" size="sm" onClick={() => go(today.slice(0, 7))}>This month</Button>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 'var(--text-md)', fontWeight: 700, color: NAVY }} aria-live="polite">{monthLabel(ym)}</div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)' }}>
            {inMonth.length === 0 ? 'Nothing this month' : `${inMonth.length} ${inMonth.length === 1 ? 'event' : 'events'}`}
            {filterLabel ? ` · ${filterLabel}` : ''}
          </div>
        </div>
      </div>

      <div className="ev-grid" role="grid" aria-label={`${monthLabel(ym)} calendar`}>
        {WEEKDAYS.map(w => <div key={w} className="ev-grid-weekday" role="columnheader">{w}</div>)}
        {cells.map(({ iso, inMonth: within }) => {
          const list = byDay.get(iso) || [];
          const overflow = list.length > MAX_CHIPS ? list.length - (MAX_CHIPS - 1) : 0;
          const shown = overflow ? list.slice(0, MAX_CHIPS - 1) : list;
          const cls = ['ev-cell', within ? '' : 'ev-cell-out', iso === today ? 'ev-cell-today' : '', iso === selected ? 'ev-cell-selected' : ''].filter(Boolean).join(' ');
          return (
            <div key={iso} className={cls} role="gridcell" aria-selected={iso === selected} onClick={() => setSelected(iso === selected ? null : iso)}>
              <button type="button" className="ev-daynum" aria-label={`${toDisplayDate(iso)}${list.length ? `, ${list.length} ${list.length === 1 ? 'event' : 'events'}` : ''}`}
                      onClick={(e) => { e.stopPropagation(); setSelected(iso === selected ? null : iso); }}>
                {Number(iso.slice(8, 10))}
              </button>
              {shown.map(ev => {
                const st = statusOf(ev.status);
                const continues = ev.event_date < iso;
                return (
                  <button key={ev.id} type="button" className={`ev-chip ev-chip-${st.tone}${continues ? ' ev-chip-cont' : ''}`} title={`${ev.title} · ${st.label}`}
                          onClick={(e) => { e.stopPropagation(); onOpen(ev); }}>
                    {ev.title}
                  </button>
                );
              })}
              {overflow > 0 && (
                <button type="button" className="ev-more" onClick={(e) => { e.stopPropagation(); setSelected(iso); }}>+{overflow} more</button>
              )}
            </div>
          );
        })}
      </div>

      {inMonth.length === 0 && (
        <div className="ev-month-empty">
          <span>Nothing in {monthLabel(ym)}{filterLabel ? ` under ${filterLabel.toLowerCase()}` : ''}.</span>
          {nearest?.next && <Button variant="ghost" size="sm" onClick={() => go(nearest.next.ym)}>{monthLabel(nearest.next.ym)}: {nearest.next.count} {nearest.next.count === 1 ? 'event' : 'events'}<ChevronRight size={14} strokeWidth={2} aria-hidden="true" /></Button>}
          {nearest?.prev && <Button variant="ghost" size="sm" onClick={() => go(nearest.prev.ym)}><ChevronLeft size={14} strokeWidth={2} aria-hidden="true" />{monthLabel(nearest.prev.ym)}: {nearest.prev.count} {nearest.prev.count === 1 ? 'event' : 'events'}</Button>}
        </div>
      )}

      {selected && (
        <section className="ev-day-panel" aria-label={`Events on ${toDisplayDate(selected)}`}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: dayEvents.length ? 8 : 0 }}>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: 'var(--text-base)' }}>
              {weekdayOf(selected)} {toDisplayDate(selected)}
              <span style={{ fontWeight: 500, color: 'var(--color-ink-muted)', fontSize: 'var(--text-sm)' }}> · {dayEvents.length === 0 ? 'nothing on this day' : `${dayEvents.length} ${dayEvents.length === 1 ? 'event' : 'events'}`}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Close</Button>
          </div>
          {dayEvents.map(ev => {
            const st = statusOf(ev.status);
            const where = [typeLabel(ev.event_type), ev.cruise_types?.length ? ev.cruise_types.join(', ') : '', ev.decks?.length ? ev.decks.join(', ') : ''].filter(Boolean).join(' · ');
            return (
              <button key={ev.id} type="button" className="ev-day-row" onClick={() => onOpen(ev)}>
                <span style={{ minWidth: 150, color: 'var(--color-ink-muted)', fontSize: 'var(--text-sm)', fontVariantNumeric: 'tabular-nums' }}>
                  {timeText(ev) || (ev.end_date && ev.end_date !== ev.event_date ? `${toDisplayDate(ev.event_date)} to ${toDisplayDate(ev.end_date)}` : 'All day')}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontWeight: 600, color: NAVY, fontSize: 'var(--text-sm)' }}>{ev.title}</span>
                  <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)' }}>{where}</span>
                </span>
                <Badge tone={st.tone}>{st.label}</Badge>
              </button>
            );
          })}
        </section>
      )}
    </div>
  );
}
